import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHandlerFn,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { OAuthAuthService } from '../data-access/oauth-auth.service';
import { Router } from '@angular/router';

const OAUTH_ENDPOINTS = [
  '/oauth2/token',
  '/oauth2/authorize',
  '/oauth2/revoke',
  '/oauth2/logout',
  '/.well-known/openid-configuration',
  '/jwks.json',
];

type HttpForwardFn = (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>;

function isOAuthRequest(url: string): boolean {
  return OAUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function withAccessToken(
  request: HttpRequest<unknown>,
  accessToken: string | null,
): HttpRequest<unknown> {
  if (!accessToken) {
    return request;
  }
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

function handleUnauthorized(router: Router, oauthAuthService: OAuthAuthService): void {
  localStorage.clear();
  sessionStorage.clear();
  router.navigate(['/login']).then(() => {
    oauthAuthService.logout();
  });
}

function forwardWithOAuth(
  request: HttpRequest<unknown>,
  forward: HttpForwardFn,
  oauthAuthService: OAuthAuthService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  if (isOAuthRequest(request.url)) {
    return forward(request);
  }

  const accessToken = oauthAuthService.getAccessToken();
  const authRequest = withAccessToken(request, accessToken);

  return forward(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || !accessToken) {
        return throwError(() => error);
      }

      if (!oauthAuthService.isAuthenticated() || oauthAuthService.isTokenExpiringSoon()) {
        handleUnauthorized(router, oauthAuthService);
        return throwError(() => error);
      }

      return from(oauthAuthService.refreshToken()).pipe(
        switchMap(() => {
          const newToken = oauthAuthService.getAccessToken();
          if (newToken && oauthAuthService.isAuthenticated()) {
            return forward(withAccessToken(request, newToken));
          }
          handleUnauthorized(router, oauthAuthService);
          return throwError(() => error);
        }),
        catchError(() => {
          handleUnauthorized(router, oauthAuthService);
          return throwError(() => error);
        }),
      );
    }),
  );
}

/**
 * Interceptor HTTP para agregar el Bearer token a las peticiones
 * Usa el access token de OAuth para autenticar requests al backend
 */
@Injectable()
export class OAuthAuthInterceptor implements HttpInterceptor {
  private readonly oauthAuthService = inject(OAuthAuthService, { optional: true });
  private readonly router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.oauthAuthService) {
      return next.handle(request);
    }
    return forwardWithOAuth(
      request,
      (requestToForward) => next.handle(requestToForward),
      this.oauthAuthService,
      this.router,
    );
  }
}

/**
 * Functional interceptor para usar con la nueva sintaxis de Angular
 */
export const oauthAuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const oauthAuthService = inject(OAuthAuthService, { optional: true });
  const router = inject(Router);

  if (!oauthAuthService) {
    return next(req);
  }

  return forwardWithOAuth(
    req,
    (requestToForward) => next(requestToForward),
    oauthAuthService,
    router,
  );
};
